import { composeLayout, defaultDraft } from '//housenumberlab.com/cdn/shop/t/37/assets/hnl-number-layout.js?v=99128771179048044031793722581';
import { currentOffer, selectedVariantId, selectionForVariant } from '//housenumberlab.com/cdn/shop/t/37/assets/hnl-number-offer.js?v=81587650471720582221793722581';
import { styles } from '//housenumberlab.com/cdn/shop/t/37/assets/hnl-number-catalog.js?v=20981300605770108511793373704';
import { appendGlyphs, materialFaces } from '//housenumberlab.com/cdn/shop/t/37/assets/hnl-number-artwork.js?v=113170903786228254451793374472';
import { prepareSnapshot } from '//housenumberlab.com/cdn/shop/t/37/assets/hnl-number-snapshot.js?v=138205951599336113391793722581#spec-file-v1';
import { createDesignCart, readDesign, lockCart } from '//housenumberlab.com/cdn/shop/t/37/assets/hnl-number-cart.js?v=162751601039027720051793722580#spec-file-v1';

const STORAGE_KEY = 'hnl-number-studio-draft-v1';
const SVG_NS = 'http://www.w3.org/2000/svg';
const requests = new Map();
const json = url => {
  if (!requests.has(url)) requests.set(url, fetch(url).then(response => {
    if (!response.ok) throw new Error('Resource unavailable');
    return response.json();
  }).catch(error => { requests.delete(url); throw error; }));
  return requests.get(url);
};
const node = (tag, attributes = {}, text = '') => {
  const element = document.createElement(tag);
  for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, value);
  element.textContent = text;
  return element;
};
const svgNode = (tag, attributes = {}, text = '') => {
  const element = document.createElementNS(SVG_NS, tag);
  for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, value);
  element.textContent = text;
  return element;
};
const inches = value => `${Number(value.toFixed(2))}″`;
const validMeasurement = value => value !== '' && Number.isFinite(Number(value)) && Number(value) > 0;

class NumberStudio extends HTMLElement {
  async connectedCallback() {
    if (this.events && !this.events.signal.aborted) return;
    this.events = new AbortController();
    this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
    this.libraries = JSON.parse(this.querySelector('[data-libraries]').textContent);
    this.storageAvailable = true;
    this.cart = createDesignCart({ root: this.dataset.rootUrl });
    this.draft = { ...defaultDraft(), heightReference: this.dataset.heightReference === 'outer' ? 'outer' : 'face' };
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      if (saved?.schemaVersion === 1 && typeof saved.address === 'string' && typeof saved.prefix === 'boolean') {
        this.draft = { ...this.draft, address: saved.address, prefix: saved.prefix,
          heightReference: saved.heightReference === undefined ? 'face' : saved.heightReference,
          style: Object.hasOwn(styles, saved.style) ? saved.style : 'americana',
          material: Object.hasOwn(materialFaces, saved.material) ? saved.material : '22K Gold',
          nominalHeightInches: Number.isInteger(saved.nominalHeightInches) && saved.nominalHeightInches >= 4 && saved.nominalHeightInches <= 9 ? saved.nominalHeightInches : 5,
          widthInches: typeof saved.widthInches === 'string' || typeof saved.widthInches === 'number' ? saved.widthInches : '',
          heightInches: typeof saved.heightInches === 'string' || typeof saved.heightInches === 'number' ? saved.heightInches : '',
          setCount: Number.isSafeInteger(saved.setCount) && saved.setCount > 0 ? saved.setCount : 1 };
      }
    } catch { this.storageAvailable = false; }
    const parameters = new URL(location.href).searchParams;
    if (parameters.has('hnl_design')) {
      try {
        const cart = await this.cart.read();
        const items = cart.items.filter(item => item.properties?._hnl_design_id === parameters.get('hnl_design') && item.properties?._hnl_revision === parameters.get('hnl_revision'));
        if (items.length !== 1) throw new Error('changed');
        const spec = await readDesign(items[0]);
        this.editing = { designId: spec.designId, revision: spec.revision };
        this.inheritedProperties = items[0].properties;
        let pending;
        try { pending = JSON.parse(sessionStorage.getItem('hnl-number-edit-v1')); } catch {}
        this.draft = { ...defaultDraft(), ...spec.draft };
        if (pending?.designId === spec.designId && pending.revision === spec.revision) this.draft = { ...this.draft, ...pending.draft };
      } catch { this.editError = true; }
    }
    if (this.dataset.mode === 'product' && Object.hasOwn(styles, this.dataset.style)) {
      this.draft.style = this.dataset.style;
      const selected = selectionForVariant(styles[this.draft.style], this.dataset.variant);
      if (selected) Object.assign(this.draft, selected);
    }
    if (!styles[this.draft.style].prefix) this.draft.prefix = false;
    this.step = this.dataset.mode === 'homepage' ? 'appearance' : 'measured';
    this.syncInputs();
    this.addEventListener('input', event => this.input(event), { signal: this.events.signal });
    this.addEventListener('change', event => this.input(event), { signal: this.events.signal });
    this.addEventListener('click', event => this.action(event), { signal: this.events.signal });
    this.showStep(this.step);
    await this.loadArtwork();
  }

  disconnectedCallback() { this.events?.abort(); this.offerRequest?.abort(); this.artworkRequest = null; }

  get style() { return styles[this.draft.style]; }

  productURL() {
    const url = new URL(`${this.dataset.rootUrl}products/${this.style.handle}`, location.origin);
    url.searchParams.set('view', 'number-studio');
    const variant = selectedVariantId(this.draft, this.style);
    if (variant) url.searchParams.set('variant', variant);
    else url.searchParams.delete('variant');
    if (this.editing) {
      url.searchParams.set('hnl_design', this.editing.designId);
      url.searchParams.set('hnl_revision', this.editing.revision);
    }
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.href;
  }

  async loadArtwork() {
    const request = Symbol();
    this.artworkRequest = request;
    this.library = null;
    this.product = null;
    this.offerRequest?.abort();
    this.querySelector('[data-scene]').replaceChildren();
    this.querySelector('[data-panes]').replaceChildren();
    this.querySelector('[data-review-drawing]').replaceChildren();
    this.querySelector('[data-action=review]').disabled = true;
    this.querySelector('[data-error]').textContent = this.copy.loading;
    this.querySelector('[data-offer]').textContent = this.copy.loading;
    this.setAttribute('aria-busy', 'true');
    try {
      const [library, policies] = await Promise.all([json(this.libraries[this.draft.style]), json(this.dataset.policies)]);
      if (!this.isConnected || request !== this.artworkRequest) return;
      this.library = library;
      this.policies = policies;
      this.render();
      if (this.step === 'confirmation') this.loadOffer();
    } catch {
      if (this.isConnected && request === this.artworkRequest) this.querySelector('[data-error]').textContent = this.copy.loading_error;
    } finally {
      if (request === this.artworkRequest) this.setAttribute('aria-busy', 'false');
    }
  }

  syncInputs() {
    for (const name of ['address', 'style', 'material', 'prefix', 'widthInches', 'heightInches', 'nominalHeightInches', 'setCount']) {
      const input = this.querySelector(`[name="${name}"]`);
      input.disabled = false;
      if (name === 'prefix') input.checked = this.draft.prefix;
      else input.value = this.draft[name];
    }
    this.querySelector('[name=prefix]').disabled = !this.style.prefix;
    const note = this.querySelector('[data-prefix-note]');
    note.hidden = this.style.prefix;
    note.textContent = this.copy.prefix_unavailable.replace('[style]', this.style.label);
    const title = this.closest('[data-number-product]')?.querySelector('[data-font-title]');
    if (title) title.textContent = this.style.label;
  }

  persist() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.draft));
      if (this.editing) sessionStorage.setItem('hnl-number-edit-v1', JSON.stringify({ ...this.editing, draft: this.draft }));
    }
    catch { this.storageAvailable = false; }
    const notice = this.querySelector('[data-storage]');
    notice.hidden = this.storageAvailable;
    notice.textContent = this.copy.storage_warning;
  }

  input(event) {
    if (this.saving) return;
    this.prepared = null;
    const input = event.target;
    if (!input.name) return;
    if (['address', 'widthInches', 'heightInches'].includes(input.name)) this.draft = { ...this.draft, [input.name]: input.value };
    else if (input.name === 'prefix') this.draft = { ...this.draft, prefix: input.checked };
    else if (input.name === 'setCount') this.draft = { ...this.draft, setCount: Number(input.value) };
    else if (input.name === 'nominalHeightInches') this.draft = { ...this.draft, nominalHeightInches: Number(input.value) };
    else if (input.name === 'material' && Object.hasOwn(materialFaces, input.value)) this.draft = { ...this.draft, material: input.value };
    else if (input.name === 'style' && Object.hasOwn(styles, input.value) && input.value !== this.draft.style) {
      this.draft = { ...this.draft, style: input.value, prefix: styles[input.value].prefix && this.draft.prefix };
      this.syncInputs();
      this.persist();
      if (this.dataset.mode === 'product' && this.storageAvailable) { location.assign(this.productURL()); return; }
      const gallery = this.closest('[data-number-product]')?.querySelector('[data-font-gallery]');
      if (gallery) gallery.hidden = this.draft.style !== this.dataset.style;
      this.loadArtwork();
      return;
    }
    this.persist();
    if (this.dataset.mode === 'product' && this.draft.style === this.dataset.style) history.replaceState(null, '', this.productURL());
    if (this.library) this.render();
  }

  action(event) {
    const action = event.target.closest('[data-action]');
    if (!action || !this.contains(action)) return;
    event.preventDefault();
    if (this.saving) return;
    if (action.dataset.action === 'save') { this.saveDesign(); return; }
    if (!this.library) return;
    if (action.dataset.action === 'measure') {
      if (!/^[0-9]{1,6}$/.test(this.draft.address)) {
        this.querySelector('[data-error]').textContent = this.copy.address_error;
        this.querySelector('[name=address]').focus();
        return;
      }
      this.persist();
      if (this.storageAvailable && this.dataset.mode === 'homepage') {
        window.location.assign(action.href);
        return;
      }
      this.showStep('measured', true);
    } else if (action.dataset.action === 'appearance') this.showStep('appearance');
    else if (action.dataset.action === 'edit') this.showStep('measured', true);
    else if (action.dataset.action === 'review' && !action.disabled) {
      this.showStep('confirmation', true);
      this.loadOffer();
    }
    this.render();
  }

  showStep(step, focus = false) {
    this.step = step;
    this.dataset.view = step;
    for (const panel of this.querySelectorAll('[data-step]')) panel.hidden = panel.dataset.step !== step;
    if (focus && step !== 'appearance') this.querySelector(step === 'measured' ? '[data-measured-heading]' : '[data-review-heading]').focus();
    this.persist();
  }

  glyphs(svg, placements, x = 0, y = 0) {
    appendGlyphs(svg, placements, this.library, this.draft.material, x, y);
  }

  appearance() {
    const example = this.draft.address === '';
    const draft = { ...this.draft, address: example ? '142' : this.draft.address, prefix: example ? false : this.draft.prefix,
      paneCount: 1, assignments: {}, widthInches: 29.5, heightInches: 8.5 };
    const pane = composeLayout(draft, this.library, this.policies).panes[0];
    const container = this.querySelector('[data-scene]');
    container.replaceChildren();
    if (pane.placements.length) {
      const scale = Math.min(1, 27.5 / pane.widthInches, 6.5 / pane.heightInches);
      const svg = svgNode('svg', { viewBox: '0 0 29.5 8.5', 'aria-hidden': 'true' });
      const artwork = svgNode('g', { transform: `translate(${(29.5 - pane.widthInches * scale) / 2} ${(8.5 - pane.heightInches * scale) / 2}) scale(${scale})` });
      this.glyphs(artwork, pane.placements);
      svg.append(artwork);
      container.append(svg);
    }
    this.querySelector('[data-scene-note]').textContent = example ? this.copy.example : this.copy.scene_note;
  }

  paneCard(pane, { width = Number(this.draft.widthInches), height = Number(this.draft.heightInches), example = false, summary = true } = {}) {
    const card = node('div', { class: 'hnl-studio__pane', 'data-fit': pane.fit, 'data-overflow': pane.overflow });
    card.append(node('h4', {}, example ? this.copy.example_glass : this.copy.glass));
    if (width > 0 && height > 0 && Number.isFinite(width) && Number.isFinite(height)) {
      const border = Math.min(width, height) * 0.004;
      const padding = border / 2;
      const svg = svgNode('svg', { viewBox: `${-padding} ${-padding} ${width + 2 * padding} ${height + 2 * padding}`,
        role: 'img', 'aria-label': `${example ? this.copy.example_glass : this.copy.glass}: ${inches(width)} × ${inches(height)}` });
      svg.append(svgNode('rect', { x: 0, y: 0, width, height, fill: '#e8eee7', stroke: pane.overflow ? '#a84835' : '#83917f', 'stroke-width': border }));
      this.glyphs(svg, pane.placements, pane.margins.left, pane.margins.top);
      card.append(svg);
      if (summary) card.append(node('p', {}, `${this.copy.glass}: ${inches(width)} × ${inches(height)}`));
    }
    if (example) card.append(node('p', { class: 'hnl-studio__small' }, this.copy.example_dimensions));
    if (summary) card.append(this.paneSummary(pane));
    return card;
  }

  paneSummary(pane) {
    const card = document.createDocumentFragment();
    if (pane.placements.length) card.append(node('p', {}, `${this.copy.number_size}: ${inches(pane.widthInches)} × ${inches(pane.heightInches)}`));
    if (pane.margins && pane.placements.length) card.append(
      node('p', {}, `${this.copy.side_space}: ${inches(pane.margins.left)}`),
      node('p', {}, `${this.copy.vertical_space}: ${inches(pane.margins.top)}`));
    if (pane.overflow) card.append(node('p', {}, this.copy.preview_overflow));
    card.append(node('p', { class: 'hnl-studio__fit' }, pane.fit === 'empty' ? this.copy.address_error
      : pane.fit === 'does_not_fit' ? this.copy.does_not_fit : pane.fit === 'fits' ? this.copy.fits : this.copy.fit_pending));
    return card;
  }

  render() {
    this.layout = composeLayout(this.draft, this.library, this.policies);
    this.querySelector('[data-policy-note]').textContent = this.draft.style === 'c-street' ? this.copy.c_street_spacing : this.copy.provisional;
    const size = this.draft.nominalHeightInches;
    const outer = Number((this.library.outerHeightInches * this.layout.artworkScale).toFixed(2));
    this.querySelector('[data-review-style]').textContent = `${this.style.label} · ${this.draft.material}`;
    this.querySelector('[data-action=measure]').href = this.productURL();
    const overall = this.layout.heightReference === 'outer';
    this.querySelector('[data-style-detail]').textContent = this.copy[overall ? 'style_detail_outer' : 'style_detail'].replace('[size]', size);
    this.querySelector('[data-review-size]').textContent = this.copy[overall ? 'size_option_outer' : 'size_option'].replace('[size]', size).replace('[outer]', outer);
    if (this.step === 'appearance') this.appearance();
    const invalidDimension = ['widthInches', 'heightInches'].some(key => this.draft[key] !== '' && !validMeasurement(this.draft[key]));
    const error = this.layout.errors.find(code => code !== 'dimensions' || (this.step !== 'appearance' && invalidDimension));
    this.querySelector('[data-error]').textContent = this.step === 'appearance' && !this.draft.address ? '' : this.copy[`${error}_error`] || '';
    this.querySelector('[name=address]').setAttribute('aria-invalid', String(this.layout.errors.includes('address') && Boolean(this.draft.address)));
    if (this.step === 'measured') {
      const width = validMeasurement(this.draft.widthInches) ? Number(this.draft.widthInches) : 32;
      const height = validMeasurement(this.draft.heightInches) ? Number(this.draft.heightInches) : 10;
      const example = this.layout.errors.includes('dimensions') || this.draft.address === '';
      const drawing = composeLayout({ ...this.draft, address: this.draft.address || '142', widthInches: width, heightInches: height }, this.library, this.policies);
      this.querySelector('[data-panes]').replaceChildren(this.paneCard(drawing.panes[0], { width, height, example, summary: false }));
      this.querySelector('[data-measure-summary]').replaceChildren(this.layout.errors.includes('dimensions')
        ? node('p', {}, this.copy.measure_prompt) : this.paneSummary(this.layout.panes[0]));
    }
    this.querySelector('[data-action=review]').disabled = this.layout.status === 'incomplete' || this.layout.status === 'does_not_fit' || this.layout.panes.some(pane => pane.overflow);
    if (this.step === 'confirmation') {
      this.querySelector('[data-review-address]').textContent = `${this.draft.prefix ? 'No. ' : ''}${this.draft.address}`;
      this.querySelector('[data-review-drawing]').replaceChildren(...this.layout.panes.map(pane => this.paneCard(pane)));
      const spec = this.querySelector('[data-review-spec]');
      spec.replaceChildren(node('dt', {}, this.copy.glass), node('dd', {}, `${inches(Number(this.draft.widthInches))} × ${inches(Number(this.draft.heightInches))}`));
      this.renderOffer();
    }
  }

  async loadOffer() {
    this.offerRequest?.abort();
    this.offerRequest = new AbortController();
    const request = this.offerRequest;
    this.product = null;
    this.querySelector('[data-offer]').textContent = this.copy.loading;
    try {
      const response = await fetch(`${this.dataset.rootUrl}products/${this.style.handle}.js`, { cache: 'no-store', signal: request.signal });
      if (!response.ok) throw new Error('Offer unavailable');
      const product = await response.json();
      if (request.signal.aborted) return;
      this.product = product;
      if (this.isConnected && this.step === 'confirmation') this.renderOffer();
    } catch { if (!request.signal.aborted && this.isConnected) this.querySelector('[data-offer]').textContent = this.copy.price_unavailable; }
  }

  renderOffer() {
    this.querySelector('[data-save]').disabled = true;
    if (!this.product) return;
    const offer = currentOffer(this.product, this.draft, this.style);
    const container = this.querySelector('[data-offer]');
    container.replaceChildren();
    if (offer.status !== 'verified') {
      container.textContent = offer.status === 'unavailable' ? this.copy.unavailable : offer.status === 'incomplete' ? this.copy.set_count_error : this.copy.price_unavailable;
      return;
    }
    const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency }).format(cents / 100);
    if (offer.billingRule === 'complete-address-set-v2') {
      container.append(node('span', {}, `${money(offer.setPriceCents)} ${this.copy.per_complete_set} × ${offer.setCount}`), node('br'));
    } else {
    container.append(node('span', {}, `${money(offer.unitPriceCents)} ${this.copy.per_digit} · ${offer.billedQuantity} ${this.copy.digits}`), node('br'));
    if (offer.prefixDigitQuantity) container.append(
      node('span', {}, `${this.copy.address_cost}: ${offer.addressDigitQuantity} × ${money(offer.unitPriceCents)} = ${money(offer.digitsSubtotalCents)}`), node('br'),
      node('span', {}, `${this.copy.prefix_cost}: ${offer.setCount} × ${money(offer.prefixUnitPriceCents)} = ${money(offer.prefixSubtotalCents)}`), node('br'));
    }
    container.append(node('strong', {}, `${this.copy.subtotal}: ${money(offer.subtotalCents)}`));
    this.querySelector('[data-save]').textContent = this.editing ? this.copy.update_cart : this.copy.save;
    this.querySelector('[data-save]').disabled = this.saving || this.editError || this.layout.status !== 'fits' || this.layout.panes.some(pane => pane.overflow);
    if (this.editError) this.querySelector('[data-save-status]').textContent = this.copy.edit_unavailable;
  }

  async saveDesign() {
    if (this.saving || this.editError || this.step !== 'confirmation' || this.layout?.status !== 'fits') return;
    this.saving = true;
    const status = this.querySelector('[data-save-status]');
    status.replaceChildren(document.createTextNode(this.copy.saving));
    const controls = [...this.querySelectorAll('input, select, button')].map(input => [input, input.disabled]);
    for (const [input] of controls) input.disabled = true;
    try {
      await lockCart(async () => {
        if (!this.prepared) {
          const response = await fetch(`${this.dataset.rootUrl}products/${this.style.handle}.js`, { cache: 'no-store' });
          if (!response.ok) throw new Error('offer');
          const product = await response.json();
          const fresh = currentOffer(product, this.draft, this.style);
          const previous = currentOffer(this.product, this.draft, this.style);
          this.product = product;
          if (fresh.status !== 'verified' || fresh.variantId !== previous.variantId || fresh.subtotalCents !== previous.subtotalCents) throw new Error('price_changed');
          this.prepared = await prepareSnapshot({ draft: this.draft, library: this.library, policies: this.policies, product,
            sceneUrl: this.querySelector('.hnl-studio__scene img').src, currency: this.dataset.currency,
            ...(this.editing ? { designId: this.editing.designId, revision: this.editing.revision + 1, inheritedProperties: this.inheritedProperties } : {}) });
        }
        await this.cart.save(this.prepared, this.editing);
      });
      try { sessionStorage.removeItem('hnl-number-edit-v1'); } catch {}
      status.textContent = this.copy.saved;
      const url = new URL(`${this.dataset.rootUrl}cart`, location.origin);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      location.assign(url.href);
    } catch (error) {
      status.textContent = error.message === 'price_changed' ? this.copy.price_changed : this.copy.save_error;
      const link = node('a', { href: `${this.dataset.rootUrl}cart` }, this.copy.view_cart);
      status.append(document.createTextNode(' '), link);
    } finally {
      this.saving = false;
      for (const [input, disabled] of controls) input.disabled = disabled;
      this.renderOffer();
    }
  }
}

if (!customElements.get('hnl-number-studio')) customElements.define('hnl-number-studio', NumberStudio);

